import { Cubit } from "blac-next";
import { PopulateUserRequest } from "@9amhealth/openapi";
import Language from "src/constants/language";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { authenticationState } from "../state";
import envVariables from "src/lib/envVariables";
import { isAppLink } from "lib/Urls";
import FunnelKey from "src/constants/funnelKey";

export interface HandoverConfig {
  targetUrl: URL;
  isAppLink: boolean;
  // Debug flag to disable redirects for testing
  redirect: boolean;
}

type KnownPartner = "lilly" | "sla";

export interface HandoverTokenState {
  isProcessing: boolean;
  error: string | null;
  config: HandoverConfig | null;
}

type PartnerConfig = {
  path: (type: PopulateUserRequest.type, lang: Language) => string;
  fallbackPath?: (type: PopulateUserRequest.type, lang: Language) => string;
};

const TYPE_MAP: Record<string, PopulateUserRequest.type | undefined> = {
  "0": PopulateUserRequest.type.DIABETES,
  "1": PopulateUserRequest.type.OBESITY
};

const LILLY_TARGET_URLS: Record<
  PopulateUserRequest.type,
  Record<Language, string>
> = {
  DIABETES: {
    [Language.en]: `https://join9am.com/lilly-diabetes`,
    [Language.es]: `https://join9am.com/es/lilly-diabetes`
  },
  OBESITY: {
    [Language.en]: `https://join9am.com/lilly-obesity`,
    [Language.es]: `https://join9am.com/es/lilly-obesity`
  }
};

const PARTNER_CONFIGS: Record<
  KnownPartner | string,
  PartnerConfig | undefined
> = {
  lilly: {
    path: (type: PopulateUserRequest.type, lang: Language) => {
      try {
        return LILLY_TARGET_URLS[type][lang];
      } catch (error) {
        reportErrorSentry(
          new Error(
            `Invalid type or language for Lilly handover: ${type}, ${lang}`
          ),
          {
            error,
            type,
            lang
          }
        );
        return "";
      }
    }
  },
  sla: {
    path: () => `/signup/${FunnelKey.solera}`,
    fallbackPath: () => `/signup/${FunnelKey.universal}`
  }
};

const initialState: HandoverTokenState = {
  isProcessing: false,
  error: null,
  config: null
};

export default class HandoverTokenCubit extends Cubit<HandoverTokenState> {
  constructor() {
    super(initialState);
  }

  processHandoverToken = async (
    searchParams: URLSearchParams,
    partner: KnownPartner | string
  ): Promise<HandoverConfig> => {
    const config: HandoverConfig = {
      targetUrl: new URL(envVariables.WEB_URL),
      isAppLink: true,
      redirect: true
    };

    const token =
      searchParams.get("token") ?? searchParams.get("lookupKey") ?? "";
    const partnerConfig = PARTNER_CONFIGS[partner];

    if (!partnerConfig) {
      reportErrorSentry(new Error(`Unknown partner: ${partner}`), {
        searchParams: searchParams.toString(),
        partner
      });
      return config;
    }
    // return {
    const typeParam = searchParams.get("type") ?? "0";
    const redirectParam = searchParams.get("redirect") ?? "";
    let language = (searchParams.get("language") ?? "en") as Language;

    if (!Object.values(Language).includes(language)) {
      language = Language.en;
    }

    const type = TYPE_MAP[typeParam] ?? PopulateUserRequest.type.DIABETES;

    let handoverPopulateSuccess = true;
    try {
      // run handover request
      if (partner === "lilly") {
        await authenticationState.sendHandoverTokenLilly({
          token,
          type
        });
      } else if (partner === "sla") {
        await authenticationState.sendHandoverTokenSolera({
          token
        });
      }
    } catch (error) {
      handoverPopulateSuccess = false;
      reportErrorSentry(error, {
        token,
        type,
        partner
      });
    }

    const pathResolver = handoverPopulateSuccess
      ? partnerConfig.path
      : (partnerConfig.fallbackPath ?? partnerConfig.path);

    const targetPath = pathResolver(type, language);
    config.redirect = redirectParam !== "0";

    const targetUrl = new URL(targetPath, envVariables.WEB_URL);

    const utm_source = searchParams.get("utm_source");
    const utm_medium = searchParams.get("utm_medium");
    const utm_campaign = searchParams.get("utm_campaign");
    const utm_content = searchParams.get("utm_content");

    targetUrl.searchParams.set("utm_source", utm_source ?? "handover");
    if (utm_medium) {
      targetUrl.searchParams.set("utm_medium", utm_medium);
    }
    if (utm_campaign) {
      targetUrl.searchParams.set("utm_campaign", utm_campaign);
    }
    if (utm_content) {
      targetUrl.searchParams.set("utm_content", utm_content);
    }
    config.targetUrl = targetUrl;
    config.isAppLink = isAppLink(targetUrl);

    if (!token) {
      reportErrorSentry(new Error("Handover token is missing"), {
        searchParams: searchParams.toString(),
        partner
      });
      return config;
    }

    return config;
  };

  clearError = () => {
    this.patch({ error: null });
  };
}
